import React, { useCallback, useEffect, useRef, useState } from "react";
import { IEnhancedSearchComponentProps } from "./EnhancedSearchComponent.types";
import {
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  IComboBox,
  IComboBoxOption,
  PrimaryButton,
  Stack,
  TooltipHost,
} from "@fluentui/react";
import {
  Category,
  EnhancedSearch,
  ICategory,
  IResult,
} from "@coherence-design-system/enhanced-search";
import { useLocation, useNavigate } from "react-router-dom";
import { useBoolean } from "@fluentui/react-hooks";
import { appInsights } from "../../services/Logging/AppInsights";
import { GlobalSearch } from "../../services/Search/GlobalSearch";
import { Items, NEW_ACTION } from "../../pages/Quotation/Common/QuotationData";
import { CustomerService } from "../../services/Customers/CustomerService";
import { useLocalStorageContext } from "../../Provider/LocalStorageProvider";

const DEFAULT_SCOPE = "All";
export const EnhancedSearchComponent: React.FunctionComponent<
  IEnhancedSearchComponentProps
> = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeScope, setActiveScope] = useState(DEFAULT_SCOPE);
  const [searchText, setSearchText] = useState("");
  const [searchingKey, setSearchingKey] = useState<string>("");
  const [hideDialog, { toggle: toggleHideDialog }] = useBoolean(true);
  const [customerId, setCustomerId] = useState<string>();

  // const originalCategories = useRef<ICategory[]>(demoCategories.slice(0, 1));
  // const scopedCategories = useRef<ICategory[]>(originalCategories.current);
  const originalCategories = useRef<ICategory[]>([]);
  const scopedCategories = useRef<ICategory[]>(originalCategories.current);
  const [currentCategories, setCurrentCategories] = useState<ICategory[]>(
    originalCategories.current
  );

  useEffect(() => {
    setSearchingKey("");
    setSearchText("");
    const state = location?.state;
    if (state && state?.hasOwnProperty("searchingKey")) {
      delete state.searchingKey;
    }
  }, [location.pathname]);

  useEffect(() => {
    if (activeScope === DEFAULT_SCOPE) {
      scopedCategories.current = originalCategories.current;
      setCurrentCategories(scopedCategories.current);
    } else {
      filterByScope();
    }
  }, [activeScope]);

  useEffect(() => {
    filterBySearchText(searchText);
  }, [searchText]);

  // Enhanced Search
  const _onSelectScope = useCallback(
    (
      _event: React.FormEvent<IComboBox>,
      option?: IComboBoxOption,
      _index?: number
    ) => {
      setActiveScope(option!.text);
    },
    []
  );

  const filterByScope = () => {
    const filterCategoriesByResults: ICategory[] =
      originalCategories.current.map((category: any) => {
        const filteredResultsPerCategory: IResult[] =
          category.items.filter(
            (result: { description: string }) =>
              result.description === activeScope.toUpperCase()
          ) ?? [];

        return {
          category: category.category,
          items: filteredResultsPerCategory.length
            ? filteredResultsPerCategory
            : [],
        };
      });

    scopedCategories.current = filterCategoriesByResults;
    setCurrentCategories(scopedCategories.current);
  };

  const filterBySearchText = (query: string) => {
    const filterByResults: ICategory[] = scopedCategories.current.map(
      (category: { category: any; items: any[] }) => ({
        category: category.category,
        items: category.items.filter((result) =>
          result.title?.toLowerCase().includes(query.toLowerCase())
        ),
      })
    );

    setCurrentCategories(filterByResults);
  };
  const _onClickSuggestionItem = (item: IResult, category?: string) => {
    appInsights.trackEvent({
      name: "In HeaderComponent.tsx, provide search suggestions.",
      properties: { searchText: item.title, category },
    });
    if (category === "Quotation") {
      if(location.pathname === "/QuotationDetail") {
        navigate(location.pathname, {
          replace: true,
          state: {
            ...location.state,
            searchText: item.itemKey,
            partitionKey: item.description,
          },
        });
      } else {
        navigate("/QuotationDetail", {
          state: {
            searchText: item.itemKey,
            partitionKey: item.description,
          },
        });
      }
    } else if (category === "Accounts") {
      const state = location.state?.state;
      if (state && state.hasOwnProperty("searchingKey")) {
        delete state.searchingKey;
        navigate(location.pathname, {
          replace: true,
          state: {
            ...state,
            actionName: NEW_ACTION,
          },
        });
      }
      switch (location.pathname) {
        case "/RecentCustomers": {
          navigate("/AddQuotation", {
            state: {
              ...state,
              actionName: NEW_ACTION,
              customerId: item.itemKey,
            },
          });
          break;
        }
        case "/AddQuotation": {
          setCustomerId(item.itemKey);
          hideDialog && toggleHideDialog();
          break;
        }
        default: {
          item.itemKey && onClickCustomerAccount(item.itemKey);
        }
      }
    }
    setSearchText("");
    setSearchingKey("");
    setCurrentCategories([]);
  };

  const onClickCustomerAccount = (customerId: string) => {
    appInsights.trackEvent({
      name: "In HeaderComponent.tsx, Clicked Customer Name button to get details of selected customer.",
      properties: { customerId: customerId },
    });
    CustomerService.getCustomerAccountsById(customerId.toString())
      .then((res) => {
        if (res !== undefined) {
          appInsights.trackEvent({
            name: "In HeaderComponent.tsx, customer name has been obtained successfully.",
            properties: { customerId },
          });
          navigate("/CustomerEditContact", { state: res.data.data });
        }
      })
      .catch((error) => {
        appInsights.trackException({
          error,
          properties: {
            name: "In HeaderComponent.tsx, error while getting customer name.",
          },
        });
      });
  };

  const renderDialog = () => {
    return (
      <Dialog
        hidden={hideDialog}
        onDismiss={toggleHideDialog}
        dialogContentProps={{
          type: DialogType.normal,
          title: "Confirmation",
          subText: `Are you sure you want to change the customer`,
        }}
      >
        <DialogFooter>
          <PrimaryButton
            onClick={() => {
              navigate(location, {
                replace: true,
                state: {
                  ...location?.state,
                  customerId,
                },
              });
              toggleHideDialog();
            }}
            text={`Yes`}
          />
          <DefaultButton onClick={() => toggleHideDialog()} text={`No`} />
        </DialogFooter>
      </Dialog>
    );
  };

  const _onChange = (
    _event?: React.ChangeEvent<HTMLInputElement>,
    newSearchText?: string
  ) => {
    // setSearchText(newSearchText || "");
    // if (!newSearchText?.trim()) setCurrentCategories([]);
    // _onCustomerChangeCallback(_event, newSearchText);
    const state = location?.state;
    setSearchText(newSearchText || "");
    setSearchingKey(newSearchText || "");
    if (!newSearchText?.trim()) setCurrentCategories([]);
    if (newSearchText && newSearchText?.length >= 3) {
      appInsights.trackTrace({
        message: "In HeaderComponent.tsx, Customer Search API call started.",
      });
      GlobalSearch.getEnhancedSearchResult(newSearchText)
        .then((response) => {
          if (response.data.code === 200 && response.data.data.length > 0) {
            /*if (
              location.pathname === "/RecentCustomers" &&
              response.data.data[0]?.category === "Accounts"
            ) {
              navigate(location.pathname, {
                replace: true,
                state: {
                  ...state,
                  actionName: NEW_ACTION,
                  searchingKey: newSearchText,
                },
              });
            } else if (response.data.data[0].category === "Quotation") {
              if (state && state?.hasOwnProperty("searchingKey")) {
                delete state.searchingKey;
                navigate(location.pathname, {
                  replace: true,
                  state: {
                    ...state,
                  },
                });
              }
            }*/
            const updatedCategories = response.data.data.map(
              (category: ICategory) => {
                return {
                  category: category.category,
                  items: category.items.map((item: IResult, index: number) => {
                    return {
                      itemKey: `${item.itemKey}-${index}`,
                      title: item.title,
                      description: item.description,
                      onItemClick: (
                        event: React.MouseEvent<HTMLLIElement, MouseEvent>,
                        itemKey: string
                      ) => {
                        return _onClickSuggestionItem(item, category.category);
                      },
                    };
                  }),
                };
              }
            );
            setCurrentCategories(updatedCategories);
            appInsights.trackTrace({
              message:
                "In HeaderComponent.tsx, Customer Search API has been executed successfully.",
            });
          } 
          else if(response.data.code === 200 && response.data.data.length === 0)
          {
            const updatedCategories: ICategory[] = [{
              category : "",
              items: [{
                itemKey: 'NoRecord-0',
                title: 'No Record',
                description: 'No matching record found',                
              }]
            }];
            setCurrentCategories(updatedCategories);
            appInsights.trackTrace({
              message:
                "In HeaderComponent.tsx, Customer Search API has been executed successfully with no records.",
            });
          }
          else {
            // setResults([]);
            setCurrentCategories([]);
            appInsights.trackTrace({
              message:
                "In HeaderComponent.tsx, Something went wrong during Customer Search API execution.",
            });
          }
        })
        .catch((error) => {
          // setResults([]);
          setCurrentCategories([]);
          appInsights.trackException({
            error,
            properties: {
              name: "In HeaderComponent.tsx, error while retrieving search result.",
            },
          });
        });
      appInsights.trackTrace({
        message:
          "API call ended: SearchAccount -> GetSearchResult() in HeaderComponent.tsx",
      });
    } else {
      appInsights.trackTrace({
        message:
          "Search will work on entering data of length greater than or equal to 3 in HeaderComponent.tsx",
      });
      setCurrentCategories([]);
    }
    navigate(location.pathname, {
      replace: true,
      state: {
        ...state,
        actionName: NEW_ACTION,
        searchingKey: newSearchText,
      },
    });
  };

  const _onClear = () => {
    setSearchText("");
    setCurrentCategories([]);
    // setCurrentCategories(scopedCategories.current);
    if (location.state && location.pathname === "/RecentCustomers") {
      setSearchingKey("");
      navigate(location.pathname, {
        replace: true,
        state: {
          ...location.state,
          searchingKey: "",
        },
      });
    }
  };
  return (
    <Stack horizontalAlign="center">
      <TooltipHost content={window.innerWidth<=768?"Search Customer Name (or) Customer ID (or) Quotation ID (or) MSX Opportunity ID":""}>
        <div style={{ width: window.innerWidth > 768 ? "600px" : "100%" }}>
          <EnhancedSearch
            minHighlightThreshold={1}
            placeholder={window.innerWidth > 768 ? "Search Customer Name (or) Customer ID (or) Quotation ID (or) MSX Opportunity ID" : "Search"}
            id="standalone-enhancedsearch-with-searchscope"
            onSearch={(newVal: string) =>
              console.log(
                `You searched ${newVal} from Enhanced Search with Search Scope`
              )
            }
            onChange={_onChange}
            onClear={_onClear}
            value={searchText}
            isSearchInHeader={true}
            // searchScopeOptions={{
            //   onItemSelect: _onSelectScope,
            //   selectedKey: activeScope,
            //   scopes: demoScopes,
            // }}
          >
            {/* {searchText.length <= 0 ? (
                <Template slot="result-items">
                  <Category category="Trending searches" items={demoTrendingSearches} resultsPerCategory={2} />
                </Template>
              ) : (
                false
              )} */}

            {searchText.length > 0 ? (
              <Stack slot="result-items">
                {currentCategories.map((category: any) => (
                  <Category key={`${category.category}`} {...category} />
                ))}
              </Stack>
            ) : (
              false
            )}
          </EnhancedSearch>
        </div>
      </TooltipHost>
      {!hideDialog && renderDialog()}
    </Stack>
  );
};